import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { mockMajorCourses } from '../../../data/mock/majorCourses.jsx';
import {
  Card,
  CardBody,
  CardGrid,
  Input,
  Select,
  Label,
  FormGroup,
  Button,
  EmptyStateCard,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  glassDeep,
  glassHover,
  PageHeader,
  PageTitle,
} from '../../../styles/components';
// Mock data for available majors
export const mockMajors = [
  {
    id: 1,
    name: "Computer Engineering",
    faculty: "Faculty of Engineering",
    description: "Focuses on the design and development of computer systems and their integration with hardware and software.",
    totalSemesters: 10,
    careers: ["Software Engineer", "Computer Architect", "Embedded Systems Developer"]
  },
  {
    id: 2,
    name: "Business Administration",
    faculty: "Faculty of Commerce",
    description: "Covers the principles of business management, finance, marketing, and organizational behavior.",
    totalSemesters: 8,
    careers: ["Business Manager", "Marketing Specialist", "Financial Analyst"]
  },
  {
    id: 3,
    name: "Graphic Design",
    faculty: "Faculty of Applied Arts",
    description: "Focuses on visual communication, design principles, and digital media creation.",
    totalSemesters: 8,
    careers: ["Graphic Designer", "UI/UX Designer", "Art Director"]
  },
  {
    id: 4,
    name: "Mechanical Engineering",
    faculty: "Faculty of Engineering",
    description: "Deals with design, analysis, and manufacturing of mechanical systems.",
    totalSemesters: 10,
    careers: ["Mechanical Engineer", "Product Designer", "Automotive Engineer"]
  },
  {
    id: 5,
    name: "Medicine",
    faculty: "Faculty of Medicine",
    description: "Studies human health, diseases, and treatments through clinical practice and biomedical research.",
    totalSemesters: 12,
    careers: ["Physician", "Surgeon", "Medical Researcher"]
  },
  {
    id: 6,
    name: "Computer Science",
    faculty: "Faculty of Science",
    description: "Focuses on algorithms, programming languages, software development, and computational theory.",
    totalSemesters: 8,
    careers: ["Software Developer", "Data Scientist", "AI Engineer"]
  },
  {
    id: 7,
    name: "Political Science",
    faculty: "Faculty of Economics and Political Science",
    description: "Studies politics, governance, and public policies at local and international levels.",
    totalSemesters: 8,
    careers: ["Political Analyst", "Diplomat", "Public Policy Advisor"]
  },
  {
    id: 8,
    name: "Architecture",
    faculty: "Faculty of Engineering",
    description: "Combines art, science, and technology to design buildings and urban environments.",
    totalSemesters: 10,
    careers: ["Architect", "Urban Planner", "Interior Designer"]
  }
];

const MajorsList = () => {
  const [majors, setMajors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMajor, setSelectedMajor] = useState(null);
  const [selectedCoursesMajor, setSelectedCoursesMajor] = useState(null);
  const [filterFaculty, setFilterFaculty] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setTimeout(() => {
      setMajors(mockMajors);
      setLoading(false);
    }, 800);
  }, []);

  const faculties = [...new Set(majors.map(major => major.faculty))];

  const filteredMajors = majors.filter(major => {
    const matchesSearch = major.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         major.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFaculty = filterFaculty === '' || major.faculty === filterFaculty;
    return matchesSearch && matchesFaculty;
  });

  const handleViewMajor = (major) => {
    setSelectedMajor(major);
  };

  const closeMajorDetails = () => {
    setSelectedMajor(null);
  };

  const handleViewCourses = (major) => {
    setSelectedCoursesMajor(major);
  };

  const closeCoursesModal = () => {
    setSelectedCoursesMajor(null);
  };

  const renderMajorCard = (major) => (
    <MajorCard key={major.id}>
      <MajorCardContent>
        <MajorName>{major.name}</MajorName>
        <FacultyBadge>{major.faculty}</FacultyBadge>
        <MajorDescription>{major.description}</MajorDescription>
        <MajorDuration>
          <strong>Duration:</strong> {major.totalSemesters} semesters
        </MajorDuration>
      </MajorCardContent>
      
      <MajorCardFooter>
        <ButtonGroup>
          <ViewDetailsButton onClick={() => handleViewMajor(major)}>
            View Details
          </ViewDetailsButton>
          <ViewCoursesButton
            onClick={(e) => {
              e.stopPropagation();
              handleViewCourses(major);
            }}
          >
            View Courses
          </ViewCoursesButton>
        </ButtonGroup>
      </MajorCardFooter>
    </MajorCard>
  );

  return (
    <MajorsContainer>
      <PageHeader>
        <PageTitle>Academic Majors</PageTitle>
      </PageHeader>

      <FiltersCard>
        <CardBody>
          <h3>Filter Majors</h3>
          
          <FilterGrid>
            <FormGroup>
              <Label htmlFor="search">Search by name or description</Label>
              <Input
                type="text" 
                id="search" 
                placeholder="Search..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="faculty">Filter by Faculty</Label>
              <Select
                id="faculty" 
                value={filterFaculty}
                onChange={(e) => setFilterFaculty(e.target.value)}
              >
                <option value="">All Faculties</option>
                {faculties.map((faculty, index) => (
                  <option key={index} value={faculty}>{faculty}</option>
                ))}
              </Select>
            </FormGroup>
          </FilterGrid>
          
          <FilterActions>
            <ClearButton 
              onClick={() => {
                setSearchTerm('');
                setFilterFaculty('');
              }}
            >
              Clear Filters
            </ClearButton>
          </FilterActions>
        </CardBody>
      </FiltersCard>

      {loading ? (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading majors...</LoadingText>
        </LoadingContainer>
      ) : (
        <MajorsGrid>
          {filteredMajors.length === 0 ? (
            <EmptyStateCard>
              <EmptyStateText>No majors match your search criteria.</EmptyStateText>
              <ViewAllButton 
                onClick={() => {
                  setSearchTerm('');
                  setFilterFaculty('');
                }}
              >
                View All Majors
              </ViewAllButton>
            </EmptyStateCard>
          ) : (
            filteredMajors.map(major => renderMajorCard(major))
          )}
        </MajorsGrid>
      )}

      {/* Major Details Modal */}
      {selectedMajor && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <StyledModalTitle>{selectedMajor.name}</StyledModalTitle>
              <CloseButton onClick={closeMajorDetails} />
            </ModalHeader>
            
            <ModalBody>
              <FacultyBadge>{selectedMajor.faculty}</FacultyBadge>
              
              <ProgramDetail>{selectedMajor.description}</ProgramDetail>
              
              <SemesterSection>
                <SemesterTitle>Program Duration</SemesterTitle>
                <ProgramDetail>
                  <strong>Total Semesters:</strong> {selectedMajor.totalSemesters}
                </ProgramDetail>
              </SemesterSection>
              
              <SemesterSection>
                <SemesterTitle>Career Opportunities</SemesterTitle>
                <CareersList>
                  {selectedMajor.careers.map((career, index) => (
                    <CareerItem key={index}>{career}</CareerItem>
                  ))}
                </CareersList>
              </SemesterSection>
            </ModalBody>
            
            <ModalActions>
              <CloseModalButton onClick={closeMajorDetails}>
                Close
              </CloseModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Courses Modal */}
      {selectedCoursesMajor && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <StyledModalTitle>{selectedCoursesMajor.name} Courses</StyledModalTitle>
              <CloseButton onClick={closeCoursesModal} />
            </ModalHeader>
            
            <ModalBody>
              <CoursesList>
                {mockMajorCourses[selectedCoursesMajor.name]?.map((course) => (
                  <CourseItem key={course.id}>
                    <CourseHeader>
                      <CourseId>{course.id}</CourseId>
                    </CourseHeader>
                    <CourseName>{course.name}</CourseName>
                    <CourseDescription>{course.description}</CourseDescription>
                  </CourseItem>
                )) || (
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    No courses available for this major.
                  </p>
                )}
              </CoursesList>
            </ModalBody>
            
            <ModalActions>
              <CloseModalButton onClick={closeCoursesModal}>
                Close
              </CloseModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </MajorsContainer>
  );
};

// Styled Components
const MajorsContainer = styled.div`
  width: 100%;
  padding: 2rem;
`;

const Header = styled.header`
  margin-bottom: 2rem;
  
  h2 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 0.5rem;
    text-shadow: 0 0 10px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3);
  }
  
  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1.1rem;
  }
`;

const FiltersCard = styled(Card)`
  ${glassDeep}
  margin-bottom: 2rem;
  background: rgba(15, 23, 42, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  
  h3 {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 1.5rem;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -0.75rem;
      left: 0;
      width: 3rem;
      height: 2px;
      background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
      border-radius: 1px;
    }
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ClearButton = styled(Button)`
  background: rgba(15, 23, 42, 0.4);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  
  &:hover {
    background: rgba(15, 23, 42, 0.6);
    transform: translateY(-2px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(99, 102, 241, 0.1);
  border-radius: 50%;
  border-top-color: ${props => props.theme.colors.primary};
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
`;

const EmptyStateText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
`;

const ViewAllButton = styled(Button)`
  background: rgba(99, 102, 241, 0.85);
  color: white;
  
  &:hover {
    background: rgba(99, 102, 241, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.25);
  }
`;

const MajorsGrid = styled(CardGrid)`
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
`;

const MajorCard = styled(Card)`
  ${glassDeep}
  ${glassHover}
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.2);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const MajorCardContent = styled(CardBody)`
  flex: 1;
`;

const MajorName = styled.h3`
  margin: 0 0 0.75rem 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
`;

const FacultyBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: rgba(99, 102, 241, 0.2);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 1rem;
  border: 1px solid rgba(99, 102, 241, 0.3);
`;

const MajorDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const MajorDuration = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
`;

const MajorCardFooter = styled.div`
  padding: 1.25rem;
  border-top: 1px solid rgba(99, 102, 241, 0.2);
  text-align: center;
`;

const ViewDetailsButton = styled(Button)`
  flex: 1;
  height: 32px;
  padding: 0 0.875rem;
  font-size: 0.813rem;
  background: rgba(99, 102, 241, 0.85);
  line-height: 1;
  
  &:hover {
    background: rgba(99, 102, 241, 0.95);
    transform: translateY(-2px);
  }
`;

const StyledModalTitle = styled(ModalTitle)`
  margin: 0 0 0.5rem 0;
  color: rgba(255, 255, 255, 0.9);
`;

const ProgramDetail = styled.p`
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
`;

const SemesterSection = styled.div`
  margin-top: 1.5rem;
`;

const SemesterTitle = styled.h4`
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 2rem;
    height: 2px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
    border-radius: 1px;
  }
`;

const CareersList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const CareerItem = styled.li`
  padding: 0.75rem 1rem;
  background-color: rgba(99, 102, 241, 0.15);
  border-radius: 6px;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(99, 102, 241, 0.2);
    transform: translateX(5px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalActions = styled(ModalFooter)`
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(99, 102, 241, 0.2);
`;

const CloseModalButton = styled(Button)`
  background: rgba(99, 102, 241, 0.85);
  
  &:hover {
    background: rgba(99, 102, 241, 0.95);
    transform: translateY(-2px);
  }
`;

const CoursesSection = styled.div`
  background: rgba(15, 23, 42, 0.4);
  margin: 1rem -1.25rem -1.25rem;
  padding: 1.25rem;
  border-top: 1px solid rgba(99, 102, 241, 0.2);
`;

const CoursesList = styled.div`
  display: grid;
  gap: 1rem;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.7);
  }
`;

const CourseItem = styled.div`
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.4);
    transform: translateY(-2px);
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CourseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const CourseId = styled.span`
  color: rgba(99, 102, 241, 0.9);
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.2rem 0.5rem;
  background: rgba(99, 102, 241, 0.15);
  border-radius: 4px;
  border: 1px solid rgba(99, 102, 241, 0.2);
`;

const CourseName = styled.h4`
  margin: 0 0 0.5rem 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
`;

const CourseDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ViewCoursesButton = styled(Button)`
  flex: 1;
  height: 32px;
  padding: 0 0.875rem;
  font-size: 0.813rem;
  background: rgba(99, 102, 241, 0.15);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
  
  &:hover {
    background: rgba(99, 102, 241, 0.25);
    transform: translateY(-2px);
  }
`;

export default MajorsList;